import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import {
  movieCategories,
  getMoviesByCategory,
  getCategoryName,
  siteInfo,
} from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setVisibleCount(12);
  }, [slug]);

  const categoryName = getCategoryName(slug || '');
  const movies = getMoviesByCategory(slug || '');
  const displayedMovies = movies.slice(0, visibleCount);
  const hasMore = visibleCount < movies.length;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - ${siteInfo.name}`,
    description: `${categoryName}高清电影大全，免费在线观看，画质清晰播放流畅不缓冲。`,
    url: `https://555mov.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: movies.map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://555mov.com/article/${movie.slug}`,
          image: movie.poster,
        },
      })),
    },
  };

  return (
    <>
      <PageSEO
        title={`${categoryName} - ${siteInfo.fullName}`}
        description={`${categoryName}高清电影大全免费在线观看，555影视提供最新${categoryName}资源，画质清晰播放流畅不缓冲。`}
        keywords={`${categoryName},555影视,高清电影,免费${categoryName},${categoryName}在线观看,最新${categoryName}`}
        canonicalUrl={`https://555mov.com/category/${slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '电影分类', href: '/category/dongzuo' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-8 bg-primary-500 rounded-full"></div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {categoryName}
              </h1>
            </div>
            <p className="text-foreground-500 text-sm ml-4">
              共 {movies.length} 部{categoryName}资源
            </p>
          </section>

          {/* Category Tabs */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {movieCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Movies Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            {displayedMovies.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {displayedMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center mt-8 pb-8">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 12)}
                      className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm"
                    >
                      加载更多 {categoryName}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <i className="ri-movie-line text-5xl block mb-4"></i>
                <p className="text-lg">该分类暂无电影资源</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium text-sm"
                >
                  返回首页查看更多电影
                </Link>
              </div>
            )}
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}